import { getArtworksPerCategory } from '../api/artCollection.api'
import { type ArtCatalog } from '../pages/ArtCollection/ArtCollection.types'
import groupBy from 'lodash/groupBy'
import { type Artwork } from '../types/art.types'
import { logger } from './logger'
import { experimentManager } from './ExperimentManager/ExperimentManager'
import partition from 'lodash/partition'
import { getUrlParam } from 'utils/UrlUtils'
import {
  FALLBACK_CATEGORY_NAMES,
  REDIRECT_CATEGORY,
} from '../constants/artCategories'

// Artists created by Mixtiles employees for the new catalog (Oded, Neil, Tal)
// All other artists are from the old catalog and should be filtered out
const ALLOWED_ARTIST_UIDS = ['vfn8fkpcqwh', 'swcn4rxps7s', 'gtc52hxp7cs']

export function isAllowedArtwork(artwork: {
  artist?: string | { _id?: string }
}): boolean {
  const artistId =
    typeof artwork?.artist === 'string' ? artwork.artist : artwork?.artist?._id
  return Boolean(artistId && ALLOWED_ARTIST_UIDS.includes(artistId))
}

const ARTWORKS_PER_CATEGORY_LIMIT = 40
export const FIRST_CATEGORY_URL_PARAM = 'category'

export function getCollectionCategoryFromUrl(
  categoryParam: string | null
): string | null {
  if (categoryParam) {
    const escapedName = categoryParam.replace(/_/g, ' ')
    return REDIRECT_CATEGORY[escapedName] || escapedName
  }
  return null
}

// Show artworks with the same set name first. Artworks without set names are shown last.
function groupArtworksBySetName(artworks: Artwork[]): Artwork[] {
  const [withSetName, withoutSetName] = partition(
    artworks,
    (artwork) => artwork.artworkSetNames && artwork.artworkSetNames.length > 0
  )
  const groupBySetName = groupBy(withSetName, 'artworkSetNames[0]')
  const sortedGroupBySetName = Object.values(groupBySetName)
    .sort((a, b) => (a.length % 2) - (b.length % 2)) // Sort even length sets first - so artworks of the same set appear together
    .map((artworks) => artworks.sort((a, b) => a.title.localeCompare(b.title)))
    .flat()
  return [...sortedGroupBySetName, ...withoutSetName]
}

const orderCatalog = (
  categories: {
    id: string
    name: string
    artworks: Artwork[]
  }[],
  pinnedCategoryNames: string[]
) => {
  const firstCategoryName = getCollectionCategoryFromUrl(
    getUrlParam(FIRST_CATEGORY_URL_PARAM)
  )
  const firstCategory = categories.find(
    (category) => category.name === firstCategoryName
  )
  if (firstCategory) {
    pinnedCategoryNames = [
      firstCategory.name,
      ...pinnedCategoryNames.filter((name) => name !== firstCategory.name),
    ]
  }

  // Divide categories into pinned and unpinned
  const [pinned, unpinned] = partition(categories, (category) =>
    pinnedCategoryNames.includes(category.name)
  )

  // Sort pinned categories in the order they appear in pinnedCategoryNames
  const pinnedCategories = pinned.sort(
    (a, b) =>
      pinnedCategoryNames.indexOf(a.name) - pinnedCategoryNames.indexOf(b.name)
  )
  const orderedCategories = [...pinnedCategories, ...unpinned]
  const orderedCatalog: ArtCatalog = {}
  orderedCategories.forEach((category) => {
    orderedCatalog[category.name] = groupArtworksBySetName(category.artworks)
  })
  return orderedCatalog
}

export async function fetchArtCatalog(): Promise<ArtCatalog> {
  const payload =
    experimentManager.getVariantPayload('art-collection-29-jan-2025') || {}
  const { categories, pinnedCategories, variant, limit } = payload
  const categoryNames =
    categories && categories?.length > 0 ? categories : FALLBACK_CATEGORY_NAMES
  const apiPerCategory = await getArtworksPerCategory({
    categoryNames: categoryNames as string[],
    limit: limit || ARTWORKS_PER_CATEGORY_LIMIT,
    variant,
  })
  if (apiPerCategory.list.length === 0) {
    throw new Error('Failed to fetch art catalog: empty catalog')
  }
  logger.info('Completed fetch art catalog')
  return orderCatalog(apiPerCategory.list, pinnedCategories || [])
}
